import React, { Children, isValidElement, useEffect } from 'react';
import type { FieldValues, FormProviderProps, UseFormReturn } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { Question } from '../components';
import { cn } from '@workspace/utils';
import { useGlobalForm } from './global-form-context';

interface QuestionProps {
    index?: number;

    [key: string]: any; // Pour permettre d'autres propriétés
}

export interface FormGlobalProps<T extends FieldValues> extends FormProviderProps<T> {
    onSubmit?: (data: T) => any;
    onPartialSubmit?: (data: T) => any;
    className?: string;
    dontPassNextStep?: boolean;
}

export function SmartFormGlobal<T extends FieldValues = FieldValues>(props: FormGlobalProps<T>) {
    const { children, onSubmit, onPartialSubmit, className, dontPassNextStep, ...methods } = props as FormGlobalProps<any>;
    const { meta, setMeta, actions } = useGlobalForm();
    const childrenArray = Children.toArray(children);
    const containsQuestion = childrenArray.some((child) => isValidElement(child) && child.type === Question);

    useEffect(() => {
        const questions = document.querySelectorAll('.step-question[data-active="true"]');

        setMeta((prev) => {
            // why: si la question courante est inactive (question conditionnelle en tête d'étape), on se place sur la
            // première question active suivante, sinon l'étape s'affiche vide
            const allQuestions = Array.from(document.querySelectorAll('.step-question'));
            const current = allQuestions[prev.subStep];
            const firstActive = allQuestions.findIndex((el, index) => index >= prev.subStep && el.getAttribute('data-active') === 'true');
            const subStep = current && current.getAttribute('data-active') !== 'true' && firstActive !== -1 ? firstActive : prev.subStep;

            return { ...prev, subStep, totalSubSteps: questions.length };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // why: une réponse peut rendre la question affichée inactive (ex. bascule recherche par immatriculation / par marque) :
    // sans correction l'étape s'affiche vide. On se place sur la question active la plus proche.
    useEffect(() => {
        const questions = Array.from(document.querySelectorAll('.step-question'));
        const current = questions[meta.subStep];
        if (!current || current.getAttribute('data-active') === 'true') return;

        let nearest = -1;
        questions.forEach((el, index) => {
            if (el.getAttribute('data-active') !== 'true') return;
            const distance = Math.abs(index - meta.subStep);
            const best = Math.abs(nearest - meta.subStep);
            // à distance égale, la question suivante l'emporte (la bascule mène à la question qui suit)
            if (nearest === -1 || distance < best || (distance === best && index > meta.subStep)) nearest = index;
        });
        if (nearest !== -1) setMeta((prev) => ({ ...prev, subStep: nearest }));
    });

    const submitHandler = async (data: any) => {
        // const result = onSubmit?.(data) ?? true;

        // why: subStep indexe toutes les questions (actives ou non) ; on est sur la dernière s'il n'y a plus de question active après
        const questions = Array.from(document.querySelectorAll('.step-question'));
        const isNextStep = !questions.some((el, index) => index > meta.subStep && el.getAttribute('data-active') === 'true');

        let result = data;

        if (isNextStep) {
            if (onSubmit) {
                result = await onSubmit(data);
            }
            if (result === false || result === undefined) {
                return;
            }

            if (!dontPassNextStep) {
                actions.saveAndGoNextStep(result);
            }
        } else {
            if (onPartialSubmit) {
                result = await onPartialSubmit(data);
            }
            if (result === false || result === undefined) {
                return;
            }

            // Sauvegarder les données même pour les sous-questions
            actions.saveAndGoNextStep(result);
        }
    };

    return (
        <FormProvider {...methods}>
            <form
                autoComplete="off"
                className={cn('flex-1 flex flex-col items-center w-full mt-6 sm:mt-10 mb-20 padding-root max-w-2xl', !containsQuestion && 'space-y-4', className)}
                onSubmit={methods.handleSubmit(submitHandler)}
            >
                {React.Children.map(childrenArray, (child, index) => {
                    if (React.isValidElement(child) && child.type === Question) {
                        // Création d'un nouvel objet de props sans utiliser l'opérateur spread sur child.props
                        const newProps = Object.assign({}, child.props, { index }) as QuestionProps;
                        return React.cloneElement(child, newProps);
                    }
                    return child;
                })}
            </form>
        </FormProvider>
    );
}

export function useSmartFormGlobal<T extends FieldValues>(): UseFormReturn<T> {
    const { state } = useGlobalForm();

    return useForm<T>({
        values: state,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldFocusError: true,
    });
}
